"use client";

import useSWR, { mutate, preload } from "swr";
import { useAuth } from "@clerk/nextjs";
import { swrFetcher } from "@/components/providers/swr-provider";

export interface Collection {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  coverImage: string | null;
  itemCount: number;
  // Sharing fields
  isPublic?: boolean;
  shareToken?: string | null;
  viewCount?: number;
  cloneCount?: number;
  starCount?: number;
  lastViewedAt?: Date | null;
  originalCollectionId?: string | null;
  originalCollection?: {
    id: string;
    name: string;
    shareToken: string | null;
    user: {
      username: string | null;
      displayName: string | null;
    };
  } | null;
  createdAt: Date;
  updatedAt: Date;
  // Collaboration
  role?: "owner" | "editor" | "viewer";
  memberCount?: number;
  members?: {
    user: {
      username: string | null;
      displayName: string | null;
      avatar: string | null;
    };
  }[];
}

export interface CollectionWithItems extends Collection {
  hasClonedItems?: boolean; // Flag indicating if collection has items not owned by user
  currentUserRole?: "owner" | "editor" | "viewer" | null;
  memberCount?: number;
  items: {
    id: string;
    userId: string; // For "Add to Library" button logic
    title: string | null;
    summary: string | null;
    imageUrl: string | null;
    persistedImageUrl: string | null;
    url: string;
    source: string | null;
    status: string;
    createdAt: Date;
    addedAt?: Date;
    commentCount: number;
    // Video and embed fields (CRITICAL for TikTok, Facebook, Instagram, LinkedIn)
    embedHtml: string | null;
    videoUrl: string | null;
    documentUrl: string | null;
    // Display fields
    author: string | null;
    type: string | null;
    category: string | null;
    note: string | null;
    tags: string[];
    // Audio fields (for music uploads)
    audioUrl: string | null;
    audioFileType: string | null;
    audioDuration: number | null;
    // Import source for content type detection
    importSource: string | null;
    // Platform metadata for performance
    platformData: any | null;
    // Domain for gamification display
    domainId: string | null;
    domain: {
      id: string;
      name: string;
      displayName: string;
      icon: string;
      color: string;
    } | null;
  }[];
}

interface CollectionsResponse {
  ok: boolean;
  data: Collection[];
}

interface CollectionResponse {
  ok: boolean;
  data: CollectionWithItems;
}

/**
 * Hook to fetch all collections for current user
 */
export function useCollections(visibility?: string) {
  const { isLoaded, userId } = useAuth();
  const shouldFetch = isLoaded && userId;

  // Build URL with visibility filter
  const url = shouldFetch
    ? `/api/collections${visibility ? `?visibility=${visibility}` : ""}`
    : null;

  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate: mutateCollections,
  } = useSWR<CollectionsResponse>(url, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
  });

  return {
    collections: data?.data || [],
    isLoading: !shouldFetch || (isLoading && !data),
    isValidating,
    error: error?.message || null,
    mutate: mutateCollections,
  };
}

/**
 * Hook to fetch single collection with items
 */
export function useCollection(collectionId: string | null) {
  const { isLoaded, userId } = useAuth();
  const shouldFetch = isLoaded && userId && collectionId;

  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate: mutateCollection,
  } = useSWR<CollectionResponse>(
    shouldFetch ? `/api/collections/${collectionId}` : null,
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
    },
  );

  return {
    collection: data?.data || null,
    isLoading: !shouldFetch || (isLoading && !data),
    isValidating,
    error: error?.message || null,
    mutate: mutateCollection,
  };
}

/**
 * Create a new collection
 * Returns { data, error } — never throws.
 */
export async function createCollection(data: {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  coverImage?: string;
}): Promise<{ data: Collection | null; error: string | null }> {
  try {
    const response = await fetch("/api/collections", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!result.ok) {
      return {
        data: null,
        error: result.error || "Failed to create collection",
      };
    }

    // Revalidate all collections list variants (with any query params)
    mutate(
      (key) => typeof key === "string" && key.startsWith("/api/collections"),
    );

    return { data: result.data, error: null };
  } catch (error) {
    console.error("Failed to create collection:", error);
    return { data: null, error: "Something went wrong. Please try again." };
  }
}

/**
 * Update a collection
 */
export async function updateCollection(
  collectionId: string,
  data: {
    name?: string;
    description?: string;
    icon?: string;
    color?: string;
    coverImage?: string;
  },
): Promise<boolean> {
  try {
    const response = await fetch(`/api/collections/${collectionId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!result.ok) {
      throw new Error(result.error || "Failed to update collection");
    }

    // Revalidate
    mutate("/api/collections");
    mutate(`/api/collections/${collectionId}`);

    return true;
  } catch (error) {
    console.error("Failed to update collection:", error);
    return false;
  }
}

/**
 * Delete a collection
 */
export async function deleteCollection(collectionId: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/collections/${collectionId}`, {
      method: "DELETE",
    });

    const result = await response.json();

    if (!result.ok) {
      throw new Error(result.error || "Failed to delete collection");
    }

    // Revalidate
    mutate("/api/collections");
    mutate((key) => typeof key === "string" && key.startsWith("/api/items"));

    return true;
  } catch (error) {
    console.error("Failed to delete collection:", error);
    return false;
  }
}

/**
 * Add items to collection
 */
export async function addItemsToCollection(
  collectionId: string,
  itemIds: string[],
): Promise<number> {
  try {
    const response = await fetch(`/api/collections/${collectionId}/items`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ itemIds }),
    });

    const result = await response.json();

    if (!result.ok) {
      throw new Error(result.error || "Failed to add items");
    }

    // Revalidate
    mutate("/api/collections");
    mutate(`/api/collections/${collectionId}`);

    return result.data.addedCount;
  } catch (error) {
    console.error("Failed to add items to collection:", error);
    return 0;
  }
}

/**
 * Remove items from collection
 */
export async function removeItemsFromCollection(
  collectionId: string,
  itemIds: string[],
): Promise<number> {
  try {
    const response = await fetch(`/api/collections/${collectionId}/items`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ itemIds }),
    });

    const result = await response.json();

    if (!result.ok) {
      throw new Error(result.error || "Failed to remove items");
    }

    // Revalidate
    mutate("/api/collections");
    mutate(`/api/collections/${collectionId}`);

    return result.data.removedCount;
  } catch (error) {
    console.error("Failed to remove items from collection:", error);
    return 0;
  }
}

/**
 * Prefetch collections for instant navigation
 */
export function prefetchCollections() {
  preload("/api/collections", swrFetcher).catch(() => {});
}
